import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { initDragScroll } from "@/lib/dragScroll";
import { LayoutGrid, Truck, Users, Store, Map, FileWarning, PoundSterling, Shield, Building2, LogOut, Sun, Moon, UserCog, KeyRound, Menu, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutGrid, end: true, testid: "nav-dashboard", section: "dashboard" },
  { to: "/vehicles", label: "Vehicles", icon: Truck, testid: "nav-vehicles", section: "vehicles" },
  { to: "/drivers", label: "Drivers", icon: Users, testid: "nav-drivers", section: "drivers" },
  { to: "/stores", label: "Stores", icon: Store, testid: "nav-stores", section: "stores" },
  { to: "/routes", label: "Route Planner", icon: Map, testid: "nav-routes", section: "routes" },
  { to: "/costs", label: "Costs", icon: PoundSterling, testid: "nav-costs", section: "costs" },
  { to: "/fines", label: "PCN Fines", icon: FileWarning, testid: "nav-fines", section: "fines" },
  { to: "/insurance", label: "Insurance", icon: Shield, testid: "nav-insurance", section: "insurance" },
  { to: "/suppliers", label: "Suppliers", icon: Building2, testid: "nav-suppliers", section: "suppliers" },
];

export default function Layout() {
  const { user, logout, can, isAdmin } = useAuth();
  const { theme, toggle } = useTheme();
  const [pwOpen, setPwOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = NAV.filter((n) => can(n.section));

  useEffect(() => { initDragScroll(); }, []);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      localStorage.setItem("sidebar-collapsed", c ? "0" : "1");
      return !c;
    });
  const closeMobile = () => setMobileOpen(false);

  const labelCls = collapsed ? "lg:hidden" : "";
  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors duration-150 ${collapsed ? "lg:justify-center lg:px-0 lg:gap-0" : ""} ${
      isActive
        ? "border-[#FF3B30] bg-muted/60 text-foreground font-semibold"
        : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60"
    }`;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 z-40 bg-background border-b border-border flex items-center gap-3 px-4">
        <button data-testid="mobile-menu-button" onClick={() => setMobileOpen(true)} className="p-1.5 -ml-1.5 text-foreground" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <div className="font-heading font-black text-lg tracking-tighter leading-none">
          LEO'S<span className="text-[#FF3B30]">FLEET</span>
        </div>
      </header>

      {mobileOpen && <div data-testid="sidebar-backdrop" className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={closeMobile} />}

      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-50 bg-background border-r border-border flex flex-col w-60 ${collapsed ? "lg:w-16" : "lg:w-60"} transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className={`border-b border-border px-4 py-5 flex items-center ${collapsed ? "lg:justify-center lg:px-0" : "justify-between"}`}>
          <div className={labelCls}>
            <div className="font-heading font-black text-xl tracking-tighter leading-none">
              LEO'S<span className="text-[#FF3B30]">FLEET</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1.5">Transport Ops</div>
          </div>
          <button
            data-testid="sidebar-toggle-button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            className="hidden lg:inline-flex text-muted-foreground hover:text-foreground transition-colors duration-150"
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <button data-testid="sidebar-close-button" onClick={closeMobile} className="lg:hidden text-muted-foreground hover:text-foreground" aria-label="Close menu">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid} className={navCls} title={label} onClick={closeMobile}>
              <Icon size={17} strokeWidth={2} />
              <span className={labelCls}>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/users" data-testid="nav-users" className={navCls} title="Users" onClick={closeMobile}>
              <UserCog size={17} strokeWidth={2} />
              <span className={labelCls}>Users</span>
            </NavLink>
          )}
        </nav>
        <div className={`border-t border-border p-4 ${collapsed ? "lg:p-3 lg:flex lg:flex-col lg:items-center" : ""}`}>
          <div className={`text-xs text-muted-foreground truncate mb-2 ${labelCls}`} data-testid="logged-in-email">{user?.email}</div>
          <NotificationsBell collapsed={collapsed} />
          <button
            data-testid="change-password-button"
            onClick={() => setPwOpen(true)}
            title="Change password"
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-150 mb-3"
          >
            <KeyRound size={14} /> <span className={labelCls}>Change password</span>
          </button>
          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-150 mb-3"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />} <span className={labelCls}>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
          </button>
          <button
            data-testid="logout-button"
            onClick={logout}
            title="Sign out"
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-[#FF3B30] transition-colors duration-150"
          >
            <LogOut size={14} /> <span className={labelCls}>Sign out</span>
          </button>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <main className={`min-w-0 pt-14 lg:pt-0 transition-[margin] duration-200 ${collapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <Outlet />
      </main>
    </div>
  );
}
